// /app — entry, mode configs, wiring (Architecture §11).
// Mode composition (Architecture §10) is not wired yet; this mounts the shell, behind the gate.
import { StrictMode, type JSX } from 'react';
import { createRoot } from 'react-dom/client';

// The design system (P2.3). Imported at the entry so every route gets it — Vite extracts these to
// a stylesheet, which is what keeps `style-src 'self'` satisfiable: no inline <style>, no runtime
// CSS-in-JS (Stack §6 bans both anyway).
import '../src/ui/styles/tokens.css';
import '../src/ui/styles/base.css';
import '../src/ui/styles/layout.css';

import { checkIsolation } from '../src/platform/isolation';
import { record } from '../src/telemetry';
import { App } from '../src/ui/App';
import { CapabilityGate } from '../src/ui/CapabilityGate';
import { defaultRenderPath } from '../src/visual/core/render-host';

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found in index.html');
}

// The assertion at boot (P0.9). Checked before the app mounts rather than inside it, because
// without isolation there is no engine — every ring from P0.3 onward needs SharedArrayBuffer, and
// SAB needs COOP/COEP. FR-42's rule is "a friendly explanation page, never a broken app", and an
// app that mounts and then fails on the first click is the broken app that rule forbids.
const isolation = checkIsolation();

if (!isolation.ok) {
  console.error(
    `[synapse] cross-origin isolation unavailable (${isolation.failure ?? 'unknown'}) — ` +
      `SharedArrayBuffer is ${isolation.sharedArrayBuffer ? 'present' : 'absent'}, ` +
      `secure context: ${String(isolation.secureContext)}. The audio engine cannot start.`,
  );
}

const root = createRoot(rootElement);

/**
 * The routes this app has (P0.12, P1.1).
 *
 * A pathname check rather than a router, because Stack v1.0 does not name one and a router bought
 * for two diagnostic pages would be a dependency arguing for its own expansion. When real routing
 * is needed, this is a handful of lines to delete.
 *
 * `import()` rather than a static import, and that is the load-bearing part: it makes each page a
 * dynamic entry, so Rollup gives it its own chunk and P0.11's `isDynamicEntry` rule keeps every
 * byte of it out of the core payload budget. Nobody boots into /bench, so nobody should download
 * it — NFR-P4 is a claim about first interactive.
 */
function mount(): void {
  const path = globalThis.location.pathname.replace(/\/$/, '');

  if (path === '/bench') {
    void import('./bench/Bench').then(({ Bench }) => {
      render(<Bench />);
    });
    return;
  }
  // `/deck-dev` was here from P2.1 to P2.7. It existed because the promoted deck had no other
  // driver; P2.7 gives it a real transport, so the harness became a second surface nobody maintains
  // and everyone half-trusts. Deleted rather than left behind a flag — an unmaintained dev route is
  // where "it works on /deck-dev" comes from.
  render(<App />);
}

function render(page: JSX.Element): void {
  root.render(
    <StrictMode>
      {isolation.ok ? page : <CapabilityGate status={isolation} />}
    </StrictMode>,
  );
}

mount();

// The one event P0.11 ships (Stack §9: "one schema'd event"). Recorded after the render call
// rather than before it, so nothing about telemetry is on the path to first paint.
//
// `record` is fire-and-forget by design: it never awaits, never throws, and if the user has opted
// out or no project key is configured — which is every environment today — the transport module is
// not even fetched. Nothing here can delay or break the app.
//
// `timeToMountMs` feeds NFR-P4's "first interactive ≤3 s on a 50 Mbps connection", which is a
// claim currently supported by nothing measured in the field.
record({
  type: 'app_booted',
  timeToMountMs: performance.now(),
  renderPath: defaultRenderPath(),
  // The context does not exist until a user gesture (P0.5), so this is genuinely unknown at boot.
  // Worth sending as null rather than guessing 48000: P0.5 came up at 44.1 kHz, and every
  // block-cadence figure in the docs assumes otherwise.
  sampleRateHz: null,
});
